import React from "react";
import { motion } from "framer-motion";
import { BadgeCheck, Bike, Laptop2, Users } from "lucide-react";
import { Reveal } from "./Reveal";
import { OwnersGallery } from "./OwnersGallery";

const ABBIE_IMG = "/abbie.webp";
const LUKE_IMG = "/luke.webp";

const OWNERS = [
  { img: LUKE_IMG, name: "Luke", tag: "The people one", testid: "owner-luke-photo", bar: "bg-[#0090D0]/[0.85]", tagColor: "text-white/80" },
  { img: ABBIE_IMG, name: "Abbie", tag: "The numbers one", testid: "owner-abbie-photo", bar: "bg-[#103080]/[0.85]", tagColor: "text-[#40C0E0]" },
];

const STATS = [
  { icon: Users, label: "Owner-led — you deal with us directly" },
  { icon: BadgeCheck, label: "ACCA (part-qualified), AAT & ICB" },
  { icon: Laptop2, label: "Abbie's a software whizz" },
];

export const OwnersStory = () => (
  <section id="about" data-testid="owners-section" className="py-24 sm:py-36 bg-[#F5F2EB] border-y border-[#103080]/10 overflow-hidden">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        <div className="grid grid-cols-2 gap-4 sm:gap-6">
          {OWNERS.map((o, i) => (
            <motion.div
              key={o.name}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: i * 0.15, ease: [0.16, 1, 0.3, 1] }}
              className="relative rounded-2xl overflow-hidden lift-shadow"
            >
              <img
                src={o.img}
                alt={`${o.name}, co-founder of Rosevale Accountants`}
                data-testid={o.testid}
                className="w-full aspect-[4/5] object-cover object-top"
              />
              <div className={`absolute bottom-0 inset-x-0 ${o.bar} backdrop-blur-sm px-4 sm:px-5 py-3 sm:py-4`}>
                <p className="font-serif text-white text-lg sm:text-xl">{o.name}</p>
                <p className={`${o.tagColor} text-[10px] sm:text-xs uppercase tracking-[0.2em]`}>{o.tag}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <div>
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-[#0090D0] font-medium mb-5">Meet the owners</p>
            <h2 className="font-serif font-semibold tracking-tight text-[#103080] text-4xl sm:text-5xl leading-tight mb-8">
              Real people. Not a call centre.
            </h2>
            <p className="text-base sm:text-lg text-[#0F172A]/70 leading-relaxed mb-6">
              Luke and Abbie are childhood sweethearts from Cornwall and Devon, married in
              2014. Their first home together was a static caravan called Rosevale — so when they
              decided to start a business together, Rosevale Accountants was born.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="space-y-6 mb-10">
              <div className="flex gap-4">
                <span className="shrink-0 w-11 h-11 rounded-full bg-[#0090D0]/10 flex items-center justify-center text-[#0090D0]">
                  <Bike size={20} strokeWidth={1.75} />
                </span>
                <p className="text-sm sm:text-base text-[#0F172A]/70 leading-relaxed">
                  <span className="font-semibold text-[#103080]">Luke</span> started out working in and
                  running a few local businesses in Cornwall, later attended Marjon University in
                  Plymouth and spent a few years teaching. He trained in payroll with the ICB and is
                  part-qualified ACCA. He and Abbie love travelling together and have taken their work
                  with them all over the world. Outside work you'll find him outdoors — ideally on a
                  bike or a coast path.
                </p>
              </div>
              <div className="flex gap-4">
                <span className="shrink-0 w-11 h-11 rounded-full bg-[#40C0E0]/15 flex items-center justify-center text-[#00B0E0]">
                  <Laptop2 size={20} strokeWidth={1.75} />
                </span>
                <p className="text-sm sm:text-base text-[#0F172A]/70 leading-relaxed">
                  <span className="font-semibold text-[#103080]">Abbie</span> went into accountancy straight
                  from school, starting out in a local accountancy practice before running the finances
                  for a local town council. She's been AAT qualified since 2011, is part-qualified ACCA,
                  and a real software whizz — if there's a problem in Xero or QuickBooks, she'll get to
                  the bottom of it. Away from the desk she's cycling, travelling or walking the coast
                  with Luke, doing her best to keep up.
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="flex flex-wrap gap-3" data-testid="owners-stats">
              {STATS.map((s) => (
                <span
                  key={s.label}
                  className="inline-flex items-center gap-2 rounded-full bg-white border border-[#103080]/10 px-5 py-2.5 text-sm font-medium text-[#103080] ambient-shadow"
                >
                  <s.icon size={15} className="text-[#0090D0]" />
                  {s.label}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </div>

      <OwnersGallery />
    </div>
  </section>
);
